import React, { useEffect, useState, useCallback } from 'react';
import { Snackbar, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const STORAGE_KEY = 'cm_pwa_install_prompted';

// Shown on mobile/desktop browsers (not running as PWA) when Chrome signals
// the app is installable. Only shown once per browser session.
const PwaInstallPrompt = ({ isPWA }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isPWA || sessionStorage.getItem(STORAGE_KEY) === '1') {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setOpen(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [isPWA]);

  const dismiss = useCallback(() => {
    setOpen(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }
    setOpen(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (isPWA) {
    return null;
  }

  return (
    <Snackbar
      open={open}
      onClose={dismiss}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', sm: 24 },
        maxWidth: 'none',
        width: { xs: 'calc(100% - 32px)', sm: 'auto' }
      }}
      ContentProps={{
        sx: {
          flexWrap: 'nowrap',
          alignItems: 'center',
          columnGap: 1,
          width: '100%'
        }
      }}
      message={
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Install Circle Moments app?
        </span>
      }
      action={
        <>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleInstall}
            sx={{
              fontWeight: 600,
              flexShrink: 0,
              borderColor: 'primary.light',
              color: 'primary.light',
              '&:hover': { borderColor: 'primary.light', backgroundColor: 'rgba(255,255,255,0.08)' }
            }}
          >
            Install
          </Button>
          <IconButton size="small" color="inherit" onClick={dismiss} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
};

export default PwaInstallPrompt;
